import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { RevealText, FadeIn } from "../components/RevealText";
import { submitContact, SUPPORT } from "../lib/api";

const SUBJECTS = ["General", "Account Help", "Deals & Points", "Bug Report", "Feedback"];

export default function SupportPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(form);
      setSent(true);
      toast.success(res.message || "Message sent!");
      setForm({ name: "", email: "", subject: "General", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please try again or email us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="support-page" className="min-h-screen">
      <section className="px-6 lg:px-10 pt-40 pb-16">
        <div className="mx-auto max-w-6xl">
          <p className="font-body uppercase tracking-[0.4em] text-xs text-brand-dark mb-8">
            Support & Contact
          </p>
          <RevealText
            as="h1"
            lines={["We're here", "to help."]}
            className="font-display font-black uppercase tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            delay={0.15}
          />
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-xl text-lg text-ink-soft leading-relaxed">
              Questions about your account, deals, points, or the app? Send us a
              message and the saveu team will get back to you.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-32">
        <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Contact details */}
          <FadeIn className="lg:col-span-4">
            <div className="h-full rounded-3xl bg-brand text-white p-10 relative overflow-hidden">
              <div className="pointer-events-none absolute -bottom-24 -right-16 w-72 h-72 rounded-full bg-white/15 blur-3xl" />
              <h2 className="font-display font-bold text-2xl tracking-tight mb-8">
                Reach us directly
              </h2>
              <a
                data-testid="support-email"
                href={`mailto:${SUPPORT.email}`}
                className="flex items-start gap-4 mb-8 group"
              >
                <span className="mt-1 shrink-0 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                  <Mail size={18} />
                </span>
                <span>
                  <span className="block text-white/70 text-xs uppercase tracking-widest mb-1">
                    Email
                  </span>
                  <span className="font-semibold break-all">{SUPPORT.email}</span>
                </span>
              </a>
              <a
                data-testid="support-phone"
                href={`tel:${SUPPORT.phone}`}
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 shrink-0 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
                  <Phone size={18} />
                </span>
                <span>
                  <span className="block text-white/70 text-xs uppercase tracking-widest mb-1">
                    Phone
                  </span>
                  <span className="font-semibold">{SUPPORT.phoneDisplay}</span>
                </span>
              </a>
              <p className="mt-10 text-white/70 text-sm leading-relaxed">
                We read every message and typically reply within one business
                day.
              </p>
            </div>
          </FadeIn>

          {/* Form */}
          <FadeIn delay={0.1} className="lg:col-span-8">
            <div className="rounded-3xl bg-white border border-ink/10 p-8 md:p-12">
              {sent ? (
                <motion.div
                  data-testid="support-success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center text-center py-16"
                >
                  <CheckCircle2 size={56} className="text-brand mb-6" />
                  <h3 className="font-display font-bold text-2xl tracking-tight text-ink">
                    Message sent!
                  </h3>
                  <p className="mt-3 text-ink-soft max-w-sm">
                    Thanks for reaching out. The saveu team will get back to you
                    at the email you provided.
                  </p>
                  <button
                    data-testid="support-send-another"
                    onClick={() => setSent(false)}
                    className="mt-8 text-brand font-semibold uppercase tracking-wider text-sm hover:underline"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="support-form" className="space-y-7">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-ink font-medium">
                        Name
                      </Label>
                      <Input
                        id="name"
                        data-testid="support-name-input"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Your name"
                        className="mt-2 h-12 rounded-xl border-ink/15 bg-paper focus-visible:ring-brand"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-ink font-medium">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="support-email-input"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@campus.edu"
                        className="mt-2 h-12 rounded-xl border-ink/15 bg-paper focus-visible:ring-brand"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-ink font-medium">
                      Topic
                    </Label>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {SUBJECTS.map((s) => (
                        <button
                          type="button"
                          key={s}
                          data-testid={`support-subject-${s.split(" ")[0].toLowerCase()}`}
                          onClick={() => setForm((f) => ({ ...f, subject: s }))}
                          className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                            form.subject === s
                              ? "bg-brand text-white border-brand"
                              : "bg-white text-ink-soft border-ink/15 hover:border-brand hover:text-brand"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-ink font-medium">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="support-message-input"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="How can we help?"
                      rows={6}
                      className="mt-2 rounded-xl border-ink/15 bg-paper focus-visible:ring-brand resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="support-submit-button"
                    disabled={loading}
                    className="glow-ring w-full md:w-auto bg-brand text-white font-body font-semibold uppercase tracking-wider px-10 py-4 rounded-full hover:-translate-y-1 transition-transform flex items-center justify-center gap-2 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send Message <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
